import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { BarChart, Download, Save, Table } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import { ApiDataService } from '/src/api/services/ApiDataService';
import {
  ApiDataFilterQueryState,
  ApiDataViewType,
  RestorableApiDataFilterState,
  TDownloadApiDataRequest,
  TFiltersApiDataRequest,
} from '/src/api/types/ApiData';
import { AddButton, Box, ChartsView, Filters, ListPage, PageHeader, TableView } from '/src/components';
import { API_DATA_FILTER_DEFAULTS, getDefaultColumns } from '/src/components/ApiData/ColumnsHelper';
import { ListSettingsButton } from '/src/components/ApiData/ListSettingsButton';
import { ToggleButtonGroup } from '/src/components/buttons/ToggleButtonGroup';
import { LoadingLayout } from '/src/components/common/LoadingLayout';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState } from '/src/hooks/useRestorableListState';
import { toastMessages } from '/src/lib/toast';
import { getPageTitle } from '/src/utils';

dayjs.extend(minMax);

const FILTER_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

const normalizeSelection = <T extends string | number>(values?: T[]) =>
  values?.length ? [...values].sort() : undefined;

const toFiltersRequest = (filters: ApiDataFilterQueryState): TFiltersApiDataRequest => ({
  chargingNetwork: normalizeSelection(filters.chargingNetwork),
  subOrganization: normalizeSelection(filters.subOrganization),
  state: normalizeSelection(filters.state),
  chargerLevel: normalizeSelection(filters.chargerLevel),
});

const keepAvailable = <T,>(selected?: T[], available?: T[]) =>
  selected && available ? selected.filter((value) => available.includes(value)) : selected;

const SERIAL_NUMBER_MIN_SEARCH_LENGTH = 3;
const SERIAL_NUMBER_MAX_RESULTS = 20;

const searchSerialNumbers = (serialNumbers: string[], query: string) => {
  const needle = query.trim().toLowerCase();
  const matches = serialNumbers.filter((serialNumber) => serialNumber.toLowerCase().includes(needle));
  const prefixMatches = matches.filter((serialNumber) => serialNumber.toLowerCase().startsWith(needle));
  const otherMatches = matches.filter((serialNumber) => !serialNumber.toLowerCase().startsWith(needle));
  return [...prefixMatches, ...otherMatches].slice(0, SERIAL_NUMBER_MAX_RESULTS);
};

export const ApiDataPage = observer(() => {
  const { t } = useTranslation();
  const userStore = useUserStore();
  const { handleApiError } = useGlobalStore();

  const restorableList = useRestorableListState<RestorableApiDataFilterState>({
    surfaceId: 'api-data-filters',
    defaults: { ...API_DATA_FILTER_DEFAULTS, hasAppliedFilters: false },
    standalone: true,
  });

  const hasAppliedFilters = restorableList.state.hasAppliedFilters;
  const filterState = useMemo<ApiDataFilterQueryState>(
    () => ({
      chargingNetwork: restorableList.state.chargingNetwork,
      subOrganization: restorableList.state.subOrganization,
      serialNumber: restorableList.state.serialNumber,
      state: restorableList.state.state,
      chargerLevel: restorableList.state.chargerLevel,
      startDate: restorableList.state.startDate,
      endDate: restorableList.state.endDate,
    }),
    [restorableList.state]
  );
  const setFilterState = restorableList.setState;

  const [view, setView] = useState(ApiDataViewType.station);
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);
  const [shownColumns, setShownColumns] = useState<string[]>([]);
  const [draftFilters, setDraftFilters] = useState<ApiDataFilterQueryState>({ ...filterState });

  const [filtersRequest, setFiltersRequest] = useState<TFiltersApiDataRequest>(() => toFiltersRequest(filterState));

  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, 'Data Api');
  }, []);

  const refreshFilterOptions = () => setFiltersRequest(toFiltersRequest(draftFilters));

  const {
    data: filtersData,
    isFetching: isFiltersFetching,
    isPlaceholderData: isFiltersPlaceholderData,
    error: queryFiltersError,
  } = useQuery({
    queryKey: ['api-data-filters', userStore.user?.id, filtersRequest],
    queryFn: () => ApiDataService.filters(filtersRequest),
    placeholderData: (prev) => prev,
    staleTime: FILTER_OPTIONS_STALE_TIME_MS,
  });

  useEffect(() => {
    if (!filtersData || isFiltersPlaceholderData) return;

    setDraftFilters((draft) => ({
      ...draft,
      chargingNetwork: keepAvailable(draft.chargingNetwork, filtersData.chargingNetwork),
      subOrganization: keepAvailable(draft.subOrganization, filtersData.subOrganization),
      chargerLevel: keepAvailable(draft.chargerLevel, filtersData.chargerLevel),
      state: keepAvailable(draft.state, filtersData.state),
      serialNumber: keepAvailable(draft.serialNumber, filtersData.serialNumber),
    }));
  }, [filtersData, isFiltersPlaceholderData]);

  const [serialNumberSearch, setSerialNumberSearch] = useState('');
  const isSerialNumberSearchTooShort = serialNumberSearch.trim().length < SERIAL_NUMBER_MIN_SEARCH_LENGTH;

  const serialNumberOptions = useMemo(
    () =>
      isSerialNumberSearchTooShort
        ? (draftFilters.serialNumber ?? [])
        : searchSerialNumbers(filtersData?.serialNumber ?? [], serialNumberSearch),
    [isSerialNumberSearchTooShort, serialNumberSearch, draftFilters.serialNumber, filtersData?.serialNumber]
  );

  const { data: columnsData, error: queryColumnsError } = useQuery({
    queryKey: ['api-data-columns', userStore.user?.id],
    queryFn: () => ApiDataService.columns(),
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (queryFiltersError) {
      handleApiError(queryFiltersError as ApiError, toastMessages.filtersApiData.error);
    }
  }, [queryFiltersError]);

  useEffect(() => {
    if (queryColumnsError) {
      handleApiError(queryColumnsError as ApiError, toastMessages.listApiData.error);
    }
  }, [queryColumnsError]);

  useEffect(() => {
    if (columnsData && columnsData.sessions.length > 0) {
      setShownColumns(getDefaultColumns(columnsData.sessions));
    }
  }, [columnsData]);

  const applyFilters = useCallback(() => {
    setFilterState({
      ...draftFilters,
      hasAppliedFilters: true,
    });
  }, [setFilterState, draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftFilters({
      ...API_DATA_FILTER_DEFAULTS,
    });
    setFilterState({ ...API_DATA_FILTER_DEFAULTS, hasAppliedFilters: false });
    setFiltersRequest(toFiltersRequest(API_DATA_FILTER_DEFAULTS));
  }, [setFilterState]);

  const handleDownloadClick = async () => {
    try {
      setIsCsvDownloading(true);
      const filters: TDownloadApiDataRequest = {
        ...filterState,
        view_type: view === ApiDataViewType.session ? 'session' : 'charger',
      };
      const response = await ApiDataService.getDownloadLink(filters);

      //Download process
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = response.url;
      document.body.appendChild(iframe);
      setTimeout(() => {
        document.body.removeChild(iframe);
        setIsCsvDownloading(false);
      }, 5000);
    } catch (err) {
      handleApiError(err as ApiError, toastMessages.downloadData.error);
      setIsCsvDownloading(false);
    }
  };

  const hasUnappliedFilters = useMemo(
    () => JSON.stringify(filterState) !== JSON.stringify(draftFilters),
    [filterState, draftFilters]
  );

  return (
    <>
      {isCsvDownloading && <LoadingLayout />}
      <ListPage
        title={t('apiData:title')}
        pageHeader={
          <PageHeader title={t('apiData:title')}>
            <ToggleButtonGroup
              options={[
                { value: ApiDataViewType.station, icon: <Save size="14px" />, label: t('apiData:chargersView') },
                { value: ApiDataViewType.session, icon: <Table size="14px" />, label: t('apiData:sessionsView') },
                { value: ApiDataViewType.chart, icon: <BarChart size="14px" />, label: t('apiData:chartView') },
              ]}
              value={view}
              onChange={(value) => setView(value as ApiDataViewType)}
            />
            <ListSettingsButton
              disabled={view !== ApiDataViewType.session}
              availableColumns={columnsData?.sessions || []}
              shownColumns={shownColumns}
              onChange={(newValues) => setShownColumns(newValues)}
            />
            <AddButton
              label={t('apiData:download')}
              icon={<Download size="14px" />}
              onClick={handleDownloadClick}
              disabled={view === ApiDataViewType.chart || !hasAppliedFilters}
            />
          </PageHeader>
        }
      >
        <Box direction="row" gap="medium" fill="horizontal">
          {view === ApiDataViewType.station && (
            <TableView
              surfaceId={'api_data_table_station}'}
              shownColumns={columnsData?.chargers || []}
              view={view}
              filters={filterState}
              hasAppliedFilters={hasAppliedFilters}
            />
          )}
          {view === ApiDataViewType.session && (
            <TableView
              surfaceId={'api_data_table_session}'}
              shownColumns={shownColumns}
              view={view}
              filters={filterState}
              hasAppliedFilters={hasAppliedFilters}
            />
          )}
          {view === ApiDataViewType.chart && <ChartsView filters={filterState} hasAppliedFilters={hasAppliedFilters} />}
          <Filters
            width={{ width: '20%', min: '20%' }}
            onSubmit={() => applyFilters()}
            onClear={() => clearFilters()}
            hasUnapplied={hasUnappliedFilters}
            submitLabel={t('apiData:searchFilters')}
            description={t('apiData:filtersDisclaimer')}
            filters={[
              {
                type: 'multi-select',
                label: t('apiData:chargingNetworkFilter'),
                values: draftFilters.chargingNetwork,
                setValues: (value) =>
                  setDraftFilters({ ...draftFilters, chargingNetwork: value as unknown as string[] }),
                onClose: refreshFilterOptions,
                isLoadingOptions: isFiltersFetching,
                options: filtersData?.chargingNetwork || [],
              },
              {
                type: 'multi-select',
                label: t('apiData:subOrganizationFilter'),
                values: draftFilters.subOrganization,
                setValues: (value) =>
                  setDraftFilters({ ...draftFilters, subOrganization: value as unknown as string[] }),
                onClose: refreshFilterOptions,
                isLoadingOptions: isFiltersFetching,
                options: filtersData?.subOrganization || [],
              },
              {
                type: 'multi-select',
                label: t('apiData:chargerLevelFilter'),
                values: draftFilters.chargerLevel,
                setValues: (value) => setDraftFilters({ ...draftFilters, chargerLevel: value.map(Number) }),
                onClose: refreshFilterOptions,
                isLoadingOptions: isFiltersFetching,
                options: filtersData?.chargerLevel?.map((item) => ({ label: String(item), value: item })) || [],
              },
              {
                type: 'multi-select',
                label: t('apiData:stateFilter'),
                values: draftFilters.state,
                setValues: (value) => setDraftFilters({ ...draftFilters, state: value as unknown as string[] }),
                onClose: refreshFilterOptions,
                isLoadingOptions: isFiltersFetching,
                options: filtersData?.state || [],
              },
              {
                type: 'multi-select',
                label: t('apiData:serialNumberFilter'),
                values: draftFilters.serialNumber,
                setValues: (value) => setDraftFilters({ ...draftFilters, serialNumber: value as unknown as string[] }),
                onSearch: setSerialNumberSearch,
                onClose: () => setSerialNumberSearch(''),
                emptySearchMessage: isSerialNumberSearchTooShort
                  ? t('apiData:serialNumberSearchHint', { min: SERIAL_NUMBER_MIN_SEARCH_LENGTH })
                  : undefined,
                isLoadingOptions: isFiltersFetching,
                options: serialNumberOptions,
              },
              {
                type: 'date',
                label: t('apiData:startDateFilter'),
                value: draftFilters.startDate,
                setValue: (value) => setDraftFilters({ ...draftFilters, startDate: value as unknown as string }),
                dateBounds: [
                  dayjs
                    .max([filtersData?.startDate, '1900-01-01'].filter(Boolean).map((d) => dayjs(d)))
                    ?.format('YYYY-MM-DD') ?? '',
                  dayjs
                    .min(
                      [draftFilters.endDate, filtersData?.endDate, '2100-01-01'].filter(Boolean).map((d) => dayjs(d))
                    )
                    ?.format('YYYY-MM-DD') ?? '',
                ],
              },
              {
                type: 'date',
                label: t('apiData:endDateFilter'),
                value: draftFilters.endDate,
                setValue: (value) => setDraftFilters({ ...draftFilters, endDate: value as unknown as string }),
                dateBounds: [
                  dayjs
                    .max(
                      [draftFilters.startDate, filtersData?.startDate, '1900-01-01']
                        .filter(Boolean)
                        .map((d) => dayjs(d))
                    )
                    ?.format('YYYY-MM-DD') ?? '',
                  dayjs
                    .min([filtersData?.endDate, '2100-01-01'].filter(Boolean).map((d) => dayjs(d)))
                    ?.format('YYYY-MM-DD') ?? '',
                ],
              },
            ]}
          />
        </Box>
      </ListPage>
    </>
  );
});

export default ApiDataPage;
